import { supabase } from '../lib/supabase';

export const tenantConfigurationService = {
  // Organization Management
  async getOrganizations() {
    const { data, error } = await supabase?.from('organizations')?.select(`
        *,
        organization_members!inner(
          id,
          role,
          joined_at,
          user_profiles!inner(
            id,
            email,
            full_name,
            role
          )
        ),
        subscriptions(
          id,
          plan_name,
          status,
          current_period_start,
          current_period_end,
          price_amount,
          currency
        )
      `)?.order('created_at', { ascending: false });

    if (error) throw error;
    
    return data?.map(org => ({
      id: org?.id,
      name: org?.name,
      slug: org?.slug,
      status: org?.status,
      description: org?.description,
      domain: org?.domain,
      logoUrl: org?.logo_url,
      settings: org?.settings,
      createdAt: org?.created_at,
      updatedAt: org?.updated_at,
      ownerId: org?.owner_id,
      members: org?.organization_members?.map(member => ({
        id: member?.id,
        role: member?.role,
        joinedAt: member?.joined_at,
        user: {
          id: member?.user_profiles?.id,
          email: member?.user_profiles?.email,
          fullName: member?.user_profiles?.full_name,
          role: member?.user_profiles?.role
        }
      })) || [],
      subscription: org?.subscriptions?.[0] ? {
        id: org?.subscriptions?.[0]?.id,
        planName: org?.subscriptions?.[0]?.plan_name,
        status: org?.subscriptions?.[0]?.status,
        currentPeriodStart: org?.subscriptions?.[0]?.current_period_start,
        currentPeriodEnd: org?.subscriptions?.[0]?.current_period_end,
        priceAmount: org?.subscriptions?.[0]?.price_amount,
        currency: org?.subscriptions?.[0]?.currency
      } : null
    })) || [];
  },

  async createOrganization(organizationData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('organizations')?.insert({
        name: organizationData?.name,
        slug: organizationData?.slug,
        description: organizationData?.description,
        domain: organizationData?.domain,
        logo_url: organizationData?.logoUrl,
        settings: organizationData?.settings || {},
        owner_id: user?.id
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      status: data?.status,
      description: data?.description,
      domain: data?.domain,
      logoUrl: data?.logo_url,
      settings: data?.settings,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      ownerId: data?.owner_id
    };
  },

  async updateOrganization(organizationId, updates) {
    const { data, error } = await supabase?.from('organizations')?.update({
        name: updates?.name,
        description: updates?.description,
        domain: updates?.domain,
        logo_url: updates?.logoUrl,
        settings: updates?.settings,
        status: updates?.status
      })?.eq('id', organizationId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      status: data?.status,
      description: data?.description,
      domain: data?.domain,
      logoUrl: data?.logo_url,
      settings: data?.settings,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      ownerId: data?.owner_id
    };
  },

  // Organization Members Management
  async getOrganizationMembers(organizationId) {
    const { data, error } = await supabase?.from('organization_members')?.select(`
        *,
        user_profiles!inner(
          id,
          email,
          full_name,
          role,
          avatar_url,
          is_active
        )
      `)?.eq('organization_id', organizationId)?.order('joined_at', { ascending: false });

    if (error) throw error;

    return data?.map(member => ({
      id: member?.id,
      organizationId: member?.organization_id,
      role: member?.role,
      joinedAt: member?.joined_at,
      user: {
        id: member?.user_profiles?.id,
        email: member?.user_profiles?.email,
        fullName: member?.user_profiles?.full_name,
        role: member?.user_profiles?.role,
        avatarUrl: member?.user_profiles?.avatar_url,
        isActive: member?.user_profiles?.is_active
      }
    })) || [];
  },

  async addOrganizationMember(organizationId, userEmail, role = 'org_user') {
    // First get the user by email
    const { data: userProfile, error: userError } = await supabase?.from('user_profiles')?.select('id')?.eq('email', userEmail)?.single();

    if (userError) throw new Error('User not found');

    const { data, error } = await supabase?.from('organization_members')?.insert({
        organization_id: organizationId,
        user_id: userProfile?.id,
        role: role
      })?.select(`
        *,
        user_profiles!inner(
          id,
          email,
          full_name,
          role,
          avatar_url,
          is_active
        )
      `)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      organizationId: data?.organization_id,
      role: data?.role,
      joinedAt: data?.joined_at,
      user: {
        id: data?.user_profiles?.id,
        email: data?.user_profiles?.email,
        fullName: data?.user_profiles?.full_name,
        role: data?.user_profiles?.role,
        avatarUrl: data?.user_profiles?.avatar_url,
        isActive: data?.user_profiles?.is_active
      }
    };
  },

  async updateOrganizationMemberRole(memberId, newRole) {
    const { data, error } = await supabase?.from('organization_members')?.update({ role: newRole })?.eq('id', memberId)?.select(`
        *,
        user_profiles!inner(
          id,
          email,
          full_name,
          role,
          avatar_url,
          is_active
        )
      `)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      organizationId: data?.organization_id,
      role: data?.role,
      joinedAt: data?.joined_at,
      user: {
        id: data?.user_profiles?.id,
        email: data?.user_profiles?.email,
        fullName: data?.user_profiles?.full_name,
        role: data?.user_profiles?.role,
        avatarUrl: data?.user_profiles?.avatar_url,
        isActive: data?.user_profiles?.is_active
      }
    };
  },

  async removeOrganizationMember(memberId) {
    const { error } = await supabase?.from('organization_members')?.delete()?.eq('id', memberId);

    if (error) throw error;
  },

  // Feature Flags Management
  async getFeatureFlags() {
    const { data, error } = await supabase?.from('feature_flags')?.select('*')?.eq('is_active', true)?.order('name');

    if (error) throw error;

    return data?.map(flag => ({
      id: flag?.id,
      name: flag?.name,
      description: flag?.description,
      flagType: flag?.flag_type,
      defaultValue: flag?.default_value,
      isActive: flag?.is_active,
      createdAt: flag?.created_at,
      updatedAt: flag?.updated_at
    })) || [];
  },

  async getOrganizationFeatureFlags(organizationId) {
    const { data, error } = await supabase?.from('organization_feature_flags')?.select(`
        *,
        feature_flags!inner(
          id,
          name,
          description,
          flag_type,
          default_value,
          is_active
        )
      `)?.eq('organization_id', organizationId);

    if (error) throw error;

    return data?.map(orgFlag => ({
      id: orgFlag?.id,
      organizationId: orgFlag?.organization_id,
      featureFlagId: orgFlag?.feature_flag_id,
      value: orgFlag?.value,
      createdAt: orgFlag?.created_at,
      updatedAt: orgFlag?.updated_at,
      featureFlag: {
        id: orgFlag?.feature_flags?.id,
        name: orgFlag?.feature_flags?.name,
        description: orgFlag?.feature_flags?.description,
        flagType: orgFlag?.feature_flags?.flag_type,
        defaultValue: orgFlag?.feature_flags?.default_value,
        isActive: orgFlag?.feature_flags?.is_active
      }
    })) || [];
  },

  async updateOrganizationFeatureFlag(organizationId, featureFlagId, value) {
    const { data, error } = await supabase?.from('organization_feature_flags')?.upsert({
        organization_id: organizationId,
        feature_flag_id: featureFlagId,
        value: value?.toString()
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      organizationId: data?.organization_id,
      featureFlagId: data?.feature_flag_id,
      value: data?.value,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Billing & Usage Management
  async getBillingUsage(organizationId) {
    const { data, error } = await supabase?.from('billing_usage')?.select('*')?.eq('organization_id', organizationId)?.order('period_start', { ascending: false });

    if (error) throw error;

    return data?.map(usage => ({
      id: usage?.id,
      organizationId: usage?.organization_id,
      metricName: usage?.metric_name,
      usageCount: usage?.usage_count,
      limitCount: usage?.limit_count,
      periodStart: usage?.period_start,
      periodEnd: usage?.period_end,
      createdAt: usage?.created_at,
      updatedAt: usage?.updated_at
    })) || [];
  },

  async getSubscription(organizationId) {
    const { data, error } = await supabase?.from('subscriptions')?.select('*')?.eq('organization_id', organizationId)?.single();

    if (error && error?.code !== 'PGRST116') throw error;

    if (!data) return null;

    return {
      id: data?.id,
      organizationId: data?.organization_id,
      planName: data?.plan_name,
      status: data?.status,
      priceAmount: data?.price_amount,
      currency: data?.currency,
      currentPeriodStart: data?.current_period_start,
      currentPeriodEnd: data?.current_period_end,
      trialEnd: data?.trial_end,
      cancelledAt: data?.cancelled_at,
      stripeSubscriptionId: data?.stripe_subscription_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateSubscription(organizationId, subscriptionData) {
    const { data, error } = await supabase?.from('subscriptions')?.upsert({
        organization_id: organizationId,
        plan_name: subscriptionData?.planName,
        status: subscriptionData?.status,
        price_amount: subscriptionData?.priceAmount,
        currency: subscriptionData?.currency,
        current_period_start: subscriptionData?.currentPeriodStart,
        current_period_end: subscriptionData?.currentPeriodEnd,
        trial_end: subscriptionData?.trialEnd,
        stripe_subscription_id: subscriptionData?.stripeSubscriptionId
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      organizationId: data?.organization_id,
      planName: data?.plan_name,
      status: data?.status,
      priceAmount: data?.price_amount,
      currency: data?.currency,
      currentPeriodStart: data?.current_period_start,
      currentPeriodEnd: data?.current_period_end,
      trialEnd: data?.trial_end,
      cancelledAt: data?.cancelled_at,
      stripeSubscriptionId: data?.stripe_subscription_id,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Audit Logs
  async getAuditLogs(organizationId, filters = {}) {
    let query = supabase?.from('audit_logs')?.select(`
        *,
        user_profiles(
          id,
          email,
          full_name
        )
      `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

    if (filters?.entityType) {
      query = query?.eq('entity_type', filters?.entityType);
    }

    if (filters?.action) {
      query = query?.eq('action', filters?.action);
    }

    if (filters?.userId) {
      query = query?.eq('user_id', filters?.userId);
    }

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(log => ({
      id: log?.id,
      organizationId: log?.organization_id,
      userId: log?.user_id,
      action: log?.action,
      entityType: log?.entity_type,
      entityId: log?.entity_id,
      oldValues: log?.old_values,
      newValues: log?.new_values,
      ipAddress: log?.ip_address,
      userAgent: log?.user_agent,
      createdAt: log?.created_at,
      user: log?.user_profiles ? {
        id: log?.user_profiles?.id,
        email: log?.user_profiles?.email,
        fullName: log?.user_profiles?.full_name
      } : null
    })) || [];
  },

  // Resource Allocation Analytics
  async getResourceAllocation(organizationId) {
    try {
      // Get subscription info for resource limits
      const subscription = await this.getSubscription(organizationId);
      
      // Get current usage metrics
      const usage = await this.getBillingUsage(organizationId);
      
      // Get member count
      const members = await this.getOrganizationMembers(organizationId);
      
      // Get recent activity from audit logs
      const recentActivity = await this.getAuditLogs(organizationId, { limit: 50 });
      
      return {
        subscription,
        usage,
        memberCount: members?.length,
        recentActivity,
        resourceMetrics: {
          storageUsed: usage?.find(u => u?.metricName === 'storage')?.usageCount || 0,
          apiCalls: usage?.find(u => u?.metricName === 'api_calls')?.usageCount || 0,
          databaseConnections: usage?.find(u => u?.metricName === 'db_connections')?.usageCount || 0
        }
      };
    } catch (error) {
      throw error;
    }
  }
};